/**
 * Sparkline (#2388, "Image-#11") — renders the pure geometry from
 * ``buildSparkline`` as a two-layer SVG: a muted full-history line plus the
 * recent segment tinted by trend (green up / red down / neutral flat). Thin
 * declarative view; all maths live in ``sparkline.ts``.
 */
import type { SparklineModel } from './sparkline';
import type { TileState } from './tileModel';

export interface SparklineProps {
  model: SparklineModel;
  state: TileState;
  width?: number;
  height?: number;
  /**
   * HOF-377 — the curve has not arrived yet, rather than not existing.
   *
   * The Preisspiegel grid's headline read (`/tiles/headlines`) carries no points:
   * ~120 per item is the bulk of the payload and the thing first paint must not
   * wait for. A tile in that state is COMPLETE — price, *Stand*, delta, Quellen —
   * and only its curve is in flight, so the slot has to say so rather than render
   * the empty treatment a tile with no history gets.
   */
  pending?: boolean;
  /**
   * Is that wait still IN PROGRESS? A failed or fallen-short histories read is
   * static, exactly as `preisspiegel-tile__skeleton--busy` already distinguishes
   * one row up — a shimmer that never resolves is a promise the page cannot keep.
   */
  pendingBusy?: boolean;
}

export default function Sparkline({
  model,
  state,
  width = 120,
  height = 32,
  pending = false,
  pendingBusy = false,
}: SparklineProps) {
  if (!model.hasData || model.historyPath === '') {
    // HOF-377 AC 13 — the same slot, at the same height, saying which of the two
    // it is. „No history" and „the curve has not arrived yet" look identical on a
    // tile that is otherwise complete, and the second must not read as the first.
    return (
      <div
        className={
          pending
            ? `preisspiegel-sparkline preisspiegel-sparkline--pending${pendingBusy ? ' preisspiegel-sparkline--pending-busy' : ''}`
            : 'preisspiegel-sparkline preisspiegel-sparkline--empty'
        }
        aria-hidden
      />
    );
  }
  return (
    <svg
      className="preisspiegel-sparkline"
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Preisverlauf (${state === 'up' ? 'steigend' : state === 'down' ? 'fallend' : 'stabil'})`}
    >
      <path className="preisspiegel-sparkline__history" d={model.historyPath} fill="none" />
      <path
        className={`preisspiegel-sparkline__recent preisspiegel-sparkline__recent--${state}`}
        d={model.recentPath}
        fill="none"
      />
    </svg>
  );
}
